import CryptoJS from 'crypto-js';
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from 'axios';
import { makeUseAxios } from 'axios-hooks';
import { fetchAuthSession } from '@aws-amplify/auth';
import { env, getBaseURL } from '@/config';
import { ServiceType, UseAxiosResponse } from '@/types/generals';

const createHttpInstance = (service: ServiceType) => {
  const instance = axios.create({
    baseURL: getBaseURL(service),
    timeout: 30000,
  });

  instance.interceptors.request.use(
    async (
      config: InternalAxiosRequestConfig<any> & {
        meta?: { service?: ServiceType };
      }
    ) => {
      const newConfig = { ...config };

      try {
        const { tokens } = await fetchAuthSession({ forceRefresh: true });

        if (tokens?.idToken) {
          newConfig.headers = newConfig.headers || {};
          newConfig.headers['Authorization'] = `Bearer ${tokens.idToken.toString()}`;
        }
      } catch (error) {
        console.error('Error fetching auth session', error);
      }

      if (!newConfig.headers) {
        newConfig.headers = new axios.AxiosHeaders();
      }

      newConfig.headers.set('X-Version', '1.0.0');

      if (service === 'paymentLink') {
        newConfig.headers.set('x-api-key', env.API_KEY_PAYMENT_LINK);
      }

      if (newConfig.data) {
        const demoToken = CryptoJS.HmacSHA256(JSON.stringify(newConfig.data), env.GW_API_REQ).toString();
        newConfig.headers['X-Signature'] = demoToken;
      }

      return newConfig;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      console.error('API Error:', error);
      return Promise.reject(error);
    }
  );

  return instance;
};

// 🟩 Export http instance pre-configured
export const httpInstance = createHttpInstance('default');
export const httpInstancePaymentLink = createHttpInstance('paymentLink');
export const httpInstanceCommerce = createHttpInstance('commerce');

// 🟩 Default httpInstance hook
export const useAxiosInstanceMake = makeUseAxios({
  axios: httpInstance,
  defaultOptions: { manual: true },
});

export const useAxiosInstance = <T = any>(
  params: string | AxiosRequestConfig
): [UseAxiosResponse<T>, (config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>] => {
  const [{ data, loading, error, response }, execute] = useAxiosInstanceMake(
    typeof params === 'string' ? params : { ...params }
  );

  return [{ data, loading, error, response }, execute];
};
